import * as Sentry from '@sentry/nextjs';

const SENTRY_DSN = process.env.NEXT_PUBLIC_SENTRY_DSN;

export function initSentry() {
  if (!SENTRY_DSN) {
    console.warn('Sentry DSN not configured. Error tracking disabled.');
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,

    // Environment
    environment: process.env.NODE_ENV,

    // Performance Monitoring
    tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,

    // Session Replay (only in production)
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Debug mode in development
    debug: process.env.NODE_ENV === 'development',

    // Filter out noisy errors
    ignoreErrors: [
      // Browser extensions
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      // Network errors that are expected
      'Failed to fetch',
      'NetworkError',
      'Load failed',
      // Firebase auth errors (handled in UI)
      'auth/popup-closed-by-user',
      'auth/cancelled-popup-request',
    ],

    // Before sending, sanitize sensitive data
    beforeSend(event, hint) {
      // Remove any PII from breadcrumbs
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => {
          if (breadcrumb.data?.email) {
            breadcrumb.data.email = '[REDACTED]';
          }
          return breadcrumb;
        });
      }

      // Don't send events for known handled errors
      const error = hint.originalException;
      if (error instanceof Error) {
        if (error.message.includes('Not authenticated')) {
          return null; // Don't report auth redirect errors
        }
      }

      return event;
    },

    // Tag events with app version
    release: process.env.NEXT_PUBLIC_APP_VERSION || 'development',
  });
}

// Helper to capture errors with context
export function captureError(
  error: Error,
  context?: {
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
    user?: { id: string; email?: string };
  }
) {
  Sentry.withScope((scope) => {
    if (context?.tags) {
      Object.entries(context.tags).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
    }

    if (context?.extra) {
      Object.entries(context.extra).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }

    if (context?.user) {
      scope.setUser({
        id: context.user.id,
        email: context.user.email ? '[REDACTED]' : undefined,
      });
    }

    Sentry.captureException(error);
  });
}

// Helper to set user context (call on login)
export function setUserContext(user: { id: string; email?: string } | null) {
  if (user) {
    Sentry.setUser({
      id: user.id,
      // Don't store email for privacy
    });
  } else {
    Sentry.setUser(null);
  }
}

// Helper to add breadcrumb
export function addBreadcrumb(
  message: string,
  category: string,
  data?: Record<string, unknown>
) {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: 'info',
  });
}
